import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  GradeLabel,
  LeaderboardPeriod,
  TeacherPracticeLeaderboardResponse,
} from '../../../shared/types';
import {
  Alert,
  EmptyState,
  LoadingBlock,
  Modal,
  PageHeader,
  Pagination,
} from '../../components/ui';
import { useApiData } from '../../hooks/useApiData';
import { currentLocale } from '../../i18n';
import { queryString } from '../../lib/api';
import { formatClock, formatDate, formatNumber } from '../../lib/format';
import { TeamPracticePeriods } from '../../components/TeamPracticePeriods';

interface ResultRow {
  room_id: string;
  room_code: string;
  room_name: string;
  mode: 'duel' | 'team_3v3';
  duration_minutes: number;
  finished_at: number;
  student_no: string;
  display_name: string;
  class_name: string;
  team_name: string | null;
  score: number;
  team_total_score: number;
  max_tile: number;
  outcome: 'win' | 'loss' | 'draw';
}

interface ResultPage {
  items: ResultRow[];
  total: number;
  pageSize: number;
}

interface ResultDetail {
  result: {
    id: string;
    code: string;
    name: string;
    mode: 'duel' | 'team_3v3';
    duration_minutes: number;
    starts_at: number;
    finished_at: number;
    finish_reason: 'time_limit' | 'all_game_over';
    winner_side: 'A' | 'B' | 'draw';
    players: Array<{
      user_id: string;
      side: 'A' | 'B';
      student_no: string;
      display_name: string;
      class_name: string;
      team_name: string | null;
      score: number;
      team_total_score: number;
      max_tile: number;
      valid_move_count: number;
      outcome: 'win' | 'loss' | 'draw';
    }>;
  };
}

function ResultDetails({ roomId, onClose }: { roomId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const details = useApiData<ResultDetail>(`/api/teacher/results/${roomId}`);
  const result = details.data?.result;
  return (
    <Modal title={result?.name ?? t('results.detailTitle')} onClose={onClose} wide>
      {details.loading ? <LoadingBlock /> : null}
      {details.error ? <Alert message={details.error} /> : null}
      {result ? (
        <div className="result-details">
          <div className="metric-grid metric-grid--compact">
            <div>
              <span>{t('results.mode')}</span>
              <strong>{t(`mode.${result.mode}`)}</strong>
            </div>
            <div>
              <span>{t('results.configuredDuration')}</span>
              <strong>
                {result.duration_minutes} {t('common.minutes')}
              </strong>
            </div>
            <div>
              <span>{t('results.actualDuration')}</span>
              <strong>{formatClock(result.finished_at - result.starts_at)}</strong>
            </div>
            <div>
              <span>{t('results.finishReason')}</span>
              <strong>{t(`results.${result.finish_reason}`)}</strong>
            </div>
          </div>
          <p className="result-details__time">
            {t('results.startedAt')}: {formatDate(result.starts_at, locale)} ·{' '}
            {t('results.finishedAt')}: {formatDate(result.finished_at, locale)}
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('results.side')}</th>
                  <th>{t('results.student')}</th>
                  <th>{t('results.className')}</th>
                  <th>{t('results.team')}</th>
                  <th>{t('results.personalScore')}</th>
                  <th>{t('results.teamScore')}</th>
                  <th>{t('results.maxTile')}</th>
                  <th>{t('results.validMoves')}</th>
                  <th>{t('results.outcome')}</th>
                </tr>
              </thead>
              <tbody>
                {result.players.map((player) => (
                  <tr key={player.user_id}>
                    <td>{player.side}</td>
                    <td>
                      <strong>{player.display_name}</strong>
                      <small className="table-subtext">{player.student_no}</small>
                    </td>
                    <td>{player.class_name}</td>
                    <td>{player.team_name ?? '—'}</td>
                    <td>{formatNumber(player.score, locale)}</td>
                    <td>{formatNumber(player.team_total_score, locale)}</td>
                    <td>{formatNumber(player.max_tile, locale)}</td>
                    <td>{formatNumber(player.valid_move_count, locale)}</td>
                    <td>
                      <span className={`outcome outcome--${player.outcome}`}>
                        {t(`results.${player.outcome}`)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function IndividualLeaderboard() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const periods = useApiData<{ items: LeaderboardPeriod[] }>('/api/teacher/leaderboard-periods');
  const gradeOptions = useApiData<{ items: GradeLabel[] }>('/api/teacher/users/grade-options');
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedPeriodId = searchParams.get('periodId') ?? '';
  const setSelectedPeriodId = (periodId: string) =>
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('periodId', periodId);
      return next;
    });
  const [gradeLevel, setGradeLevel] = useState('');
  const [practiceMode, setPracticeMode] = useState<'unlimited' | 'timed_3m'>('unlimited');

  const effectivePeriodId = periods.data?.items.some((period) => period.id === selectedPeriodId)
    ? selectedPeriodId
    : (periods.data?.items.find((period) => period.status === 'active')?.id ??
      periods.data?.items[0]?.id ??
      '');

  const rankingPath = effectivePeriodId
    ? `/api/teacher/leaderboards/practice${queryString({
        periodId: effectivePeriodId,
        gradeLevel,
        mode: practiceMode,
      })}`
    : null;
  const ranking = useApiData<TeacherPracticeLeaderboardResponse>(rankingPath);

  return (
    <>
      {periods.error ? <Alert message={periods.error} /> : null}
      <section className="leaderboard-admin-toolbar card">
        <label className="field">
          <span>{t('leaderboard.selectPeriod')}</span>
          <select
            value={effectivePeriodId}
            onChange={(event) => setSelectedPeriodId(event.target.value)}
          >
            {!periods.data?.items.length ? (
              <option value="">{t('leaderboard.noPeriods')}</option>
            ) : null}
            {periods.data?.items.map((period) => (
              <option key={period.id} value={period.id}>
                {period.name} · {t(`status.${period.status}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('leaderboard.gradeFilter')}</span>
          <select value={gradeLevel} onChange={(event) => setGradeLevel(event.target.value)}>
            <option value="">{t('leaderboard.allGrades')}</option>
            {(gradeOptions.data?.items ?? []).map((grade) => (
              <option key={grade} value={grade}>
                {grade === 'K'
                  ? t('leaderboard.kindergarten')
                  : t('leaderboard.gradeLabel', { grade })}
              </option>
            ))}
          </select>
        </label>
        <Link
          className="button button--ghost"
          to={`/teacher/seasons?type=individual&periodId=${encodeURIComponent(effectivePeriodId)}`}
        >
          {t('seasons.manage')}
        </Link>
      </section>
      <div
        className="tab-list tab-list--secondary"
        role="tablist"
        aria-label={t('leaderboard.modeView')}
      >
        {(['unlimited', 'timed_3m'] as const).map((mode) => (
          <button
            key={mode}
            type="button"
            role="tab"
            className={`tab-button ${practiceMode === mode ? 'is-active' : ''}`}
            aria-selected={practiceMode === mode}
            onClick={() => setPracticeMode(mode)}
          >
            {t(mode === 'unlimited' ? 'leaderboard.unlimitedMode' : 'leaderboard.timedMode')}
          </button>
        ))}
      </div>
      {ranking.error ? <Alert message={ranking.error} /> : null}
      {!periods.loading && !periods.error && !periods.data?.items.length ? (
        <EmptyState title={t('leaderboard.noPeriods')} />
      ) : null}
      {ranking.loading ||
      (ranking.data &&
        (ranking.data.mode !== practiceMode || ranking.data.period.id !== effectivePeriodId)) ? (
        <LoadingBlock />
      ) : null}
      {ranking.data?.mode === practiceMode && ranking.data.period.id === effectivePeriodId ? (
        <section className="leaderboard-ranking">
          <div className="leaderboard-ranking__header">
            <div>
              <h2>{ranking.data.period.name}</h2>
              <p>
                {gradeLevel
                  ? gradeLevel === 'K'
                    ? t('leaderboard.kindergarten')
                    : t('leaderboard.gradeLabel', { grade: gradeLevel })
                  : t('leaderboard.allGrades')}
              </p>
            </div>
            <strong>
              {t('leaderboard.participantSummary', {
                count: ranking.data.participantCount,
              })}
            </strong>
          </div>
          <Alert
            message={t(
              practiceMode === 'unlimited'
                ? 'leaderboard.unlimitedResultsNote'
                : 'leaderboard.timedResultsNote',
            )}
            tone="info"
          />
          {ranking.data.entries.length ? (
            <div className="table-wrap card">
              <table>
                <thead>
                  <tr>
                    <th>{t('leaderboard.rank')}</th>
                    <th>{t('users.studentNumber')}</th>
                    <th>{t('users.name')}</th>
                    <th>{t('users.className')}</th>
                    <th>{t('users.grade')}</th>
                    <th>
                      {t(
                        practiceMode === 'unlimited' ? 'common.score' : 'leaderboard.averageScore',
                      )}
                    </th>
                    <th>
                      {t(practiceMode === 'unlimited' ? 'common.maxTile' : 'leaderboard.gameCount')}
                    </th>
                    {practiceMode === 'unlimited' ? <th>{t('results.validMoves')}</th> : null}
                    {practiceMode === 'unlimited' ? <th>{t('leaderboard.completedAt')}</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {ranking.data.entries.map((entry) => (
                    <tr key={entry.studentId}>
                      <td>
                        <strong>{entry.rank}</strong>
                      </td>
                      <td>{entry.studentNumber}</td>
                      <td>{entry.name}</td>
                      <td>{entry.className}</td>
                      <td>
                        {entry.gradeLevel === null
                          ? '—'
                          : entry.gradeLevel === 'K'
                            ? t('leaderboard.kindergarten')
                            : t('leaderboard.gradeLabel', { grade: entry.gradeLevel })}
                      </td>
                      <td>{formatNumber(entry.score, locale)}</td>
                      <td>
                        {formatNumber(
                          practiceMode === 'unlimited' ? (entry.maxTile ?? 0) : entry.gameCount,
                          locale,
                        )}
                      </td>
                      {practiceMode === 'unlimited' ? (
                        <td>{formatNumber(entry.validMoveCount ?? 0, locale)}</td>
                      ) : null}
                      {practiceMode === 'unlimited' ? (
                        <td>{formatDate(entry.endedAt, locale)}</td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              title={t(
                practiceMode === 'unlimited'
                  ? 'leaderboard.noResults'
                  : 'leaderboard.noTimedResults',
              )}
            />
          )}
        </section>
      ) : null}
    </>
  );
}

export function TeacherResultsPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const views = ['matches', 'leaderboard', 'teamStandings', 'teamMatches'] as const;
  const view = views.find((value) => value === searchParams.get('view')) ?? 'matches';
  const setView = (nextView: typeof view) =>
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set('view', nextView);
      next.delete('roomId');
      if ((view === 'leaderboard') !== (nextView === 'leaderboard')) next.delete('periodId');
      return next;
    });
  const [page, setPage] = useState(1);
  const [mode, setMode] = useState('');
  const [query, setQuery] = useState('');
  const [className, setClassName] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const detailId = searchParams.get('roomId');
  const setDetailId = (roomId: string | null) =>
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      if (roomId) next.set('roomId', roomId);
      else next.delete('roomId');
      return next;
    });
  const filters = useMemo(
    () => ({ mode, query, className, from, to }),
    [mode, query, className, from, to],
  );
  const path = useMemo(
    () =>
      view === 'matches'
        ? `/api/teacher/results${queryString({ page, pageSize: 20, ...filters })}`
        : null,
    [filters, page, view],
  );
  const exportQuery = queryString(filters);
  const results = useApiData<ResultPage>(path);

  return (
    <>
      <PageHeader
        title={t('results.title')}
        subtitle={t(view === 'matches' ? 'results.subtitle' : 'leaderboard.teacherSubtitle')}
        actions={
          view === 'matches' ? (
            <div className="button-group">
              <a
                className="button button--ghost"
                href={`/api/teacher/results/export.csv${exportQuery}`}
              >
                {t('results.exportCsv')}
              </a>
              <a
                className="button button--primary"
                href={`/api/teacher/results/export.xlsx${exportQuery}`}
              >
                {t('results.exportXlsx')}
              </a>
            </div>
          ) : view === 'leaderboard' ? (
            <a className="button button--ghost" href="/api/teacher/timed-practice/export.csv">
              {t('results.exportTimedRaw')}
            </a>
          ) : null
        }
      />
      <div
        className="tab-list results-view-tabs"
        role="tablist"
        aria-label={t('leaderboard.teacherResultsView')}
      >
        {views.map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            className={`tab-button ${view === item ? 'is-active' : ''}`}
            aria-selected={view === item}
            onClick={() => setView(item)}
          >
            {t(
              item === 'matches'
                ? 'leaderboard.matchResults'
                : item === 'leaderboard'
                  ? 'leaderboard.individualBoard'
                  : item === 'teamStandings'
                    ? 'leaderboard.teamBoard'
                    : 'seasons.teamMatches',
            )}
          </button>
        ))}
      </div>
      {view === 'leaderboard' ? (
        <IndividualLeaderboard />
      ) : view === 'teamStandings' || view === 'teamMatches' ? (
        <TeamPracticePeriods
          teacher
          resultsView={view === 'teamStandings' ? 'standings' : 'matches'}
        />
      ) : (
        <>
          <section className="toolbar toolbar--wrap card">
            <input
              value={query}
              placeholder={t('common.search')}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
            />
            <select
              value={mode}
              aria-label={t('results.mode')}
              onChange={(event) => {
                setMode(event.target.value);
                setPage(1);
              }}
            >
              <option value="">{t('common.all')}</option>
              <option value="duel">{t('mode.duel')}</option>
              <option value="team_3v3">{t('mode.team_3v3')}</option>
            </select>
            <input
              value={className}
              placeholder={t('results.classFilter')}
              onChange={(event) => {
                setClassName(event.target.value);
                setPage(1);
              }}
            />
            <label className="inline-field">
              <span>{t('results.from')}</span>
              <input
                type="date"
                value={from}
                onChange={(event) => {
                  setFrom(event.target.value);
                  setPage(1);
                }}
              />
            </label>
            <label className="inline-field">
              <span>{t('results.to')}</span>
              <input
                type="date"
                value={to}
                onChange={(event) => {
                  setTo(event.target.value);
                  setPage(1);
                }}
              />
            </label>
          </section>
          {results.error ? <Alert message={results.error} /> : null}
          {results.loading ? (
            <LoadingBlock />
          ) : results.data?.items.length ? (
            <>
              <div className="table-wrap card">
                <table>
                  <thead>
                    <tr>
                      <th>{t('results.room')}</th>
                      <th>{t('results.mode')}</th>
                      <th>{t('results.configuredDuration')}</th>
                      <th>{t('results.student')}</th>
                      <th>{t('results.className')}</th>
                      <th>{t('results.team')}</th>
                      <th>{t('results.personalScore')}</th>
                      <th>{t('results.teamScore')}</th>
                      <th>{t('results.maxTile')}</th>
                      <th>{t('results.outcome')}</th>
                      <th>{t('results.occurredAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.data.items.map((result, index) => (
                      <tr key={`${result.room_id}-${result.student_no}-${index}`}>
                        <td>
                          <button
                            type="button"
                            className="text-button"
                            onClick={() => setDetailId(result.room_id)}
                          >
                            {result.room_name}
                          </button>
                          <small className="table-subtext">{result.room_code}</small>
                        </td>
                        <td>{t(`mode.${result.mode}`)}</td>
                        <td>
                          {result.duration_minutes} {t('common.minutes')}
                        </td>
                        <td>
                          <strong>{result.display_name}</strong>
                          <small className="table-subtext">{result.student_no}</small>
                        </td>
                        <td>{result.class_name}</td>
                        <td>{result.team_name ?? '—'}</td>
                        <td>{formatNumber(result.score, locale)}</td>
                        <td>{formatNumber(result.team_total_score, locale)}</td>
                        <td>{result.max_tile}</td>
                        <td>
                          <span className={`outcome outcome--${result.outcome}`}>
                            {t(`results.${result.outcome}`)}
                          </span>
                        </td>
                        <td>{formatDate(result.finished_at, locale)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={page}
                total={results.data.total}
                pageSize={results.data.pageSize}
                onPage={setPage}
              />
            </>
          ) : (
            <EmptyState title={t('results.noResults')} />
          )}
        </>
      )}
      {detailId ? <ResultDetails roomId={detailId} onClose={() => setDetailId(null)} /> : null}
    </>
  );
}
